import React from 'react';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Plus, Pencil, Trash2, Clock, ArrowRight } from 'lucide-react';
import ConfirmDialog from '../ConfirmDialog';
import DelegationDialog from './DelegationDialog';
import { useAuth } from '../../context/authContext';
import { useToast } from '../ui/use-toast';
import {
    createDelegation,
    updateDelegation,
    deleteDelegation,
} from '../../services/delegation.service';
import { formatCalendarDate } from '../../utils/dates';

const DelegationStatus = ({ active }) => (
    <div className="flex items-center gap-2">
        <span
            className={`h-2 w-2 rounded-full shrink-0 ${active ? 'bg-yavrio-green' : 'bg-amber-500'}`}
        />
        <span className={`text-sm font-medium ${active ? 'text-emerald-700' : 'text-amber-700'}`}>
            {active ? 'Active' : 'Scheduled'}
        </span>
    </div>
);

export default function DelegationsManager({ delegations, users, onChanged }) {

    const { user } = useAuth();
    const { toast } = useToast();

    const handleCreate = async (data) => {
        const response = await createDelegation(user.token, user.organisationId, data);
        if (response && response.status === 201) {
            toast({ title: 'Success', description: 'Delegation created', variant: 'success' });
            onChanged && onChanged();
            return true;
        }
        const message = response?.data?.error?.message || 'There was an error creating the delegation';
        toast({ title: 'Error', description: message, variant: 'destructive' });
        return false;
    };

    const handleUpdate = async (delegationId, data) => {
        const response = await updateDelegation(user.token, user.organisationId, delegationId, data);
        if (response && response.status === 200) {
            toast({ title: 'Success', description: 'Delegation updated', variant: 'success' });
            onChanged && onChanged();
            return true;
        }
        const message = response?.data?.error?.message || 'There was an error updating the delegation';
        toast({ title: 'Error', description: message, variant: 'destructive' });
        return false;
    };

    const handleDelete = async (delegationId) => {
        const response = await deleteDelegation(user.token, user.organisationId, delegationId);
        if (response && response.status === 200) {
            toast({ title: 'Success', description: 'Delegation removed', variant: 'success' });
            onChanged && onChanged();
        } else {
            const message = response?.data?.error?.message || 'There was an error removing the delegation';
            toast({ title: 'Error', description: message, variant: 'destructive' });
        }
    };

    return (
        <Card>
            <CardHeader>
                <div className="flex flex-row items-start justify-between gap-4">
                    <div className="flex flex-col gap-1 min-w-0">
                        <div className="flex items-center gap-2">
                            <Clock className="w-5 h-5 text-yavrio-blue shrink-0" />
                            <h3 className="my-0">Delegations</h3>
                        </div>
                        <p className="text-sm text-gray-500 mb-0">
                            Temporarily reassign a user&apos;s required approvals to another user for a fixed date
                            range, such as during leave.
                        </p>
                    </div>
                    <DelegationDialog users={users} onSave={handleCreate}>
                        <Button className="yvr-button gap-2 shrink-0">
                            <Plus className="w-4 h-4" /> New Delegation
                        </Button>
                    </DelegationDialog>
                </div>
            </CardHeader>
            <CardContent>
                {delegations.length === 0 ? (
                    <p className="text-sm text-gray-500 mb-0">No delegations yet.</p>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow className="hover:bg-white">
                                <TableHead>From</TableHead>
                                <TableHead>To</TableHead>
                                <TableHead>Period</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead></TableHead>
                                <TableHead></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {delegations.map((delegation) => (
                                <TableRow key={delegation.id} className="hover:bg-white">
                                    <TableCell>
                                        <div className="flex flex-col">
                                            <span className="text-sm font-medium">{delegation.fromUser?.name}</span>
                                            <span className="text-xs text-gray-500">{delegation.fromUser?.email}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-2">
                                            <ArrowRight className="w-4 h-4 text-slate-300 shrink-0" />
                                            <div className="flex flex-col">
                                                <span className="text-sm font-medium">{delegation.toUser?.name}</span>
                                                <span className="text-xs text-gray-500">{delegation.toUser?.email}</span>
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <span className="text-sm text-slate-600">
                                            {formatCalendarDate(delegation.startDate)} – {formatCalendarDate(delegation.endDate)}
                                        </span>
                                    </TableCell>
                                    <TableCell>
                                        <DelegationStatus active={delegation.active} />
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <DelegationDialog
                                            delegation={delegation}
                                            users={users}
                                            onSave={(data) => handleUpdate(delegation.id, data)}
                                        >
                                            <Button variant="ghost" className="p-2 text-yavrio-blue">
                                                <Pencil className="h-4 w-4" />
                                            </Button>
                                        </DelegationDialog>
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <ConfirmDialog
                                            title="Remove Delegation"
                                            description={
                                                <>
                                                    {`Remove the delegation from `}
                                                    <b>{delegation.fromUser?.name}</b>
                                                    {` to `}
                                                    <b>{delegation.toUser?.name}</b>
                                                    {`?`}
                                                </>
                                            }
                                            cancel="Cancel"
                                            action="Remove"
                                            variant="destructive"
                                            handleAction={() => handleDelete(delegation.id)}
                                        >
                                            <Button variant="ghost" className="p-2 text-red-400">
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </ConfirmDialog>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </CardContent>
        </Card>
    );
}
